import { useCallback, useEffect, useState } from "react";
import { ModalDialog } from "../components/ModalDialog";
import { lazyComponent } from "../components/lazy";
import { onOpenSettingsDialog } from "./settingsDialog";
import { onSettingsTabRequest, type SettingsTabId } from "./settingsTabRequest";
import { riskStage } from "./riskStage";
import { DiscardRiskChanges } from "./DiscardRiskChanges";
import "./settingsDialog.css";

const SettingsPanel = lazyComponent(() => import("./SettingsPanel").then((m) => m.SettingsPanel), "settings");

/**
 * Window-wide Settings (owner 2026-10-04): a large centred dialog with its groups listed down the
 * left, opened from the top bar's gear, the palette or the coach banner. Escape or "Return to
 * trading" closes it; with unsaved Risk limits changes it first asks "Discard unsaved risk
 * changes?". Ctrl+Shift+F (Exit All) always closes it at once. It is never a dock module, so
 * opening or closing it never changes the saved layout (saved Settings tabs are dropped on read,
 * `dropPanelsOfKind`).
 */
export function SettingsDialogHost(): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<SettingsTabId>("connection");
  const [asking, setAsking] = useState(false);
  const closeNow = useCallback(() => {
    setAsking(false);
    setOpen(false);
  }, []);
  const close = useCallback(() => {
    if (riskStage.leave() === "prompt") setAsking(true);
    else closeNow();
  }, [closeNow]);
  const closeForExit = useCallback(() => {
    // Exit All never waits behind a question: staged risk edits are dropped.
    riskStage.discardAll();
    closeNow();
  }, [closeNow]);
  useEffect(() => onSettingsTabRequest(setTab), []);
  useEffect(
    () =>
      onOpenSettingsDialog(() => {
        setOpen(true);
      }),
    [],
  );
  if (!open) return null;
  return (
    <ModalDialog
      title="Settings"
      onClose={close}
      onCloseForExit={closeForExit}
      className="settings-dialog"
      returnFocus='button[aria-label="Settings"]'
    >
      <SettingsPanel initialTab={tab} />
      {asking && (
        <DiscardRiskChanges
          onCancel={() => {
            setAsking(false);
          }}
          onDiscard={() => {
            riskStage.discardAll();
            closeNow();
          }}
        />
      )}
    </ModalDialog>
  );
}
